import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '~/app/hooks';
import { fetchLogo } from '~/store/main/actions';
import { logoSelector } from '~/store/main/selectors';
import { LoginForm } from './components/LoginForm';
import { CloudStyled, LoginStyled } from './styled';

import Clock from './components/Clock';
import Alert from '~/components/common/Alert';
import { clearNotification } from '~/store/main';
import { useLocation } from 'react-router-dom';
import { Language, changeLanguage } from '~/store/layout';
import { getLanguage } from '~/utils/sessionStorage';
import TokenTransferApi from '~/api/tokenTransfer';
import history from '~/history';
import config from '~/config';
import Loader from '~/components/common/Loader';
import { loginAction } from '~/store/auth/actions';
import { AppDispatch } from '~/store';

const Login: FunctionComponent = () => {
  const lang = useAppSelector((state) => state.layout.lang);
  const localLang = getLanguage();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const logo = useAppSelector(logoSelector);
  const [ssoLoading, setSsoLoading] = useState(true);
  const [ssoError, setSsoError] = useState<string | null>(null);

  const isSSO = location.pathname === '/login';

  useEffect(() => {
    document.documentElement.setAttribute('dir', lang === Language.he || localLang === Language.he ? 'rtl' : 'ltr');
  }, [lang]);

  useEffect(() => {
    if (localLang) {
      dispatch(changeLanguage(localLang as Language));
    }
  }, [localLang]);

  useEffect(() => {
    if (!logo) {
      dispatch(fetchLogo());
    }
    dispatch(clearNotification());
  }, []);

  // SSO handling for /login route
  useEffect(() => {
    if (!isSSO) {
      setSsoLoading(false);
      return;
    }

    const handleSSO = async () => {
      const searchParams = new URLSearchParams(location.search);
      const transferKey = searchParams.get('transferKey');

      if (transferKey) {
        // Received transferKey from app - fetch tokens (stored as HTTP-only cookies by server)
        try {
          const userData = await TokenTransferApi.getTokensByTransferKey(transferKey);

          // Clean URL
          const url = new URL(window.location.href);
          url.searchParams.delete('transferKey');
          window.history.replaceState({}, document.title, url.pathname);

          // Dispatch loginAction with user data from server response
          await dispatch(loginAction(userData));
          history.goToEmployeeLogin();
        } catch (err) {
          console.error('SSO auth failed:', err);
          setSsoError('Failed to authenticate via SSO. Please try again.');
          setSsoLoading(false);
        }
      } else {
        // No transferKey - redirect to app for SSO
        const returnUrl = encodeURIComponent(window.location.origin + '/login');
        window.location.href = `${config.appUrl}/sso?returnUrl=${returnUrl}`;
      }
    };

    handleSSO();
  }, [isSSO, location.search, dispatch]);

  // For employee login (/employeeLogin), show the form
  if (!isSSO) {
    return (
      <LoginStyled>
        <CloudStyled />
        <Alert />
        <LoginForm logo={logo} />
        <Clock />
      </LoginStyled>
    );
  }

  // For manager login, show loading or error
  if (ssoLoading) {
    return (
      <LoginStyled>
        <CloudStyled />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column',
          }}
        >
          <Loader isLoading={true} />
          <p style={{ marginTop: '20px', color: '#fff' }}>Redirecting to login...</p>
        </div>
      </LoginStyled>
    );
  }

  if (ssoError) {
    return (
      <LoginStyled>
        <CloudStyled />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column',
          }}
        >
          <p style={{ color: '#fff' }}>{ssoError}</p>
          <button
            onClick={() => {
              const returnUrl = encodeURIComponent(window.location.origin + '/login');
              window.location.href = `${config.appUrl}/sso?returnUrl=${returnUrl}`;
            }}
            style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}
          >
            Retry Login
          </button>
        </div>
      </LoginStyled>
    );
  }

  return null;
};

export default Login;
